import { useMemo, useState } from "react";
import { Plus, Search, X, Check, LayoutGrid, Image as ImageIcon, Hash, BarChart3, Info, Compass, Wallet, Shapes } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useGet } from "../lib/hooks";
import { Button } from "./ui/button";
import { LibraryWidgetView } from "./dashboard/LibraryWidgets";
import { WIDGETS } from "./dashboard/Widgets";
import { BUILTIN_GROUPS, BuiltinPreview, Tag, isLive } from "./dashboard/BuiltinPreviews";

const uid = () => Math.random().toString(36).slice(2, 10);
const CAT_ICON = { Banners: ImageIcon, "Headline numbers": Hash, "Charts & activity": BarChart3, Information: Info, "Navigation & resources": Compass, Finance: Wallet };
const STATUS_TONE = { "Built-in": "border-[#C2A462]/50 text-[#EBD6A0]", Released: "border-emerald-400/40 text-emerald-300", Draft: "border-sky-400/40 text-sky-300" };

function AddToDashboard({ item, dashboards, onDone }) {
  const [target, setTarget] = useState("");
  const add = async () => {
    const d = dashboards.find((x) => x.id === target);
    try {
      await api.put(`/dashboards/${d.id}`, { ...d, widgets: [...(d.widgets || []), { id: uid(), width: "full", ...item.make() }] });
      toast.success(`Added "${item.name}" to ${d.name}`); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select className="h-9 flex-1 rounded-md border border-[#C2A462]/25 bg-[#1d2a46] px-2 text-xs" value={target} onChange={(e) => setTarget(e.target.value)} data-testid={`library-target-${item.key}`}>
        <option value="">Choose a dashboard…</option>
        {dashboards.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
      </select>
      <Button size="sm" disabled={!target} onClick={add} data-testid={`library-add-${item.key}`}><Plus className="h-3.5 w-3.5 mr-1" />Add to dashboard</Button>
    </div>
  );
}

const Status = ({ s }) => <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATUS_TONE[s]}`}>{s}</span>;

function Tile({ item, active, onOpen }) {
  const Icon = CAT_ICON[item.category] || Shapes;
  return (
    <button onClick={onOpen} data-testid={`library-tile-${item.key}`}
      className={`group flex flex-col rounded-lg border p-4 text-left transition-[border-color,transform] hover:-translate-y-0.5 ${active ? "border-[#C2A462] bg-[#18233b]" : "border-white/10 bg-[#111a2c] hover:border-[#C2A462]/50"}`}>
      <div className="flex items-start justify-between"><div className="rounded-md bg-[#C2A462]/15 p-2"><Icon className="h-5 w-5 text-[#C2A462]" /></div><Status s={item.status} /></div>
      <div className="mt-3 text-sm font-semibold text-slate-100">{item.name}</div>
      <div className="mt-1 line-clamp-2 min-h-[2.2rem] text-xs text-slate-400">{item.desc}</div>
      <div className="mt-3 flex items-center gap-1.5">
        <span className="rounded border border-white/10 px-1.5 py-0.5 text-[10px] text-slate-400">{item.category}</span>
        <span className="rounded border border-white/10 px-1.5 py-0.5 text-[10px] text-slate-400">{item.source}</span>
        <span className={`ml-auto rounded-full border p-1 ${active ? "border-[#C2A462] bg-[#C2A462] text-[#0b1220]" : "border-white/20 text-slate-300 group-hover:border-[#C2A462]"}`}>{active ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}</span>
      </div>
    </button>
  );
}

function Detail({ item, dashboards, onAdded, onClose }) {
  const Icon = CAT_ICON[item.category] || Shapes;
  return (
    <aside className="rounded-lg border border-[#C2A462]/30 bg-[#16213a] p-5 space-y-4 lg:sticky lg:top-4" data-testid="library-detail">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-[#C2A462]/15 p-2.5"><Icon className="h-6 w-6 text-[#C2A462]" /></div>
        <div className="flex-1"><div className="flex flex-wrap items-center gap-2"><div className="text-lg font-semibold text-[#EBD6A0]">{item.name}</div><Status s={item.status} /></div>
          <div className="text-xs text-slate-400 mt-0.5">{item.desc}</div></div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-100" data-testid="library-detail-close"><X className="h-4 w-4" /></button>
      </div>
      <div className="max-h-[380px] overflow-auto rounded-md border border-white/10 bg-[#0b1220] p-3" data-testid="library-detail-preview">{item.preview()}</div>
      <div className="grid grid-cols-[110px_1fr] gap-y-1.5 text-xs">
        <span className="text-slate-500">Category</span><span className="text-slate-200">{item.category}</span>
        <span className="text-slate-500">Data source</span><span className="text-slate-200">{item.source}</span>
        <span className="text-slate-500">Preview data</span><span>{item.tag}</span>
      </div>
      <AddToDashboard item={item} dashboards={dashboards} onDone={onAdded} />
    </aside>
  );
}

function useItems(lib, lab) {
  return useMemo(() => {
    const built = !lab ? [] : BUILTIN_GROUPS.flatMap(([cat, items]) => items.map(([type, desc]) => ({
      key: `builtin-${type}`, name: WIDGETS[type].label.split(" (")[0], desc, category: cat, source: "BOS HQ", status: "Built-in",
      tag: <Tag live={isLive(type)} />, preview: () => <BuiltinPreview type={type} />, make: () => ({ type, config: { ...WIDGETS[type].defaults } }),
    })));
    const gen = (lib || []).map((w) => ({
      key: w.id, name: w.name, desc: w.subtitle || w.description || "Shared widget", category: w.category || "General",
      source: /xero|financ|revenue/i.test(w.type || "") ? "Xero" : "BOS HQ", status: !lab || w.status === "final" ? "Released" : "Draft",
      tag: <span className="text-slate-300">Your organisation's data</span>, preview: () => <LibraryWidgetView def={w} d={null} />,
      make: () => ({ type: "library", config: { widget_def_id: w.id } }),
    }));
    return [...built, ...gen];
  }, [lib, lab]);
}

export function WidgetLibraryTab({ dashboards, onAdded, lab }) {
  const [lib] = useGet("/widget-library");
  const items = useItems(lib, lab);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [status, setStatus] = useState("all");
  const [sel, setSel] = useState(null);
  const cats = [...new Set(items.map((i) => i.category))];
  const shown = items.filter((i) => (cat === "all" || i.category === cat) && (status === "all" || i.status === status)
    && (!q || `${i.name} ${i.desc} ${i.category}`.toLowerCase().includes(q.toLowerCase())));
  const current = items.find((i) => i.key === sel);
  if (lib && !items.length) return <p className="text-sm text-slate-500" data-testid="widget-library-empty">No widgets are available on your plan yet.</p>;
  return (
    <div className="space-y-4" data-testid="widget-library">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search widgets (e.g. revenue, key dates, links…)" data-testid="library-search"
            className="h-9 w-full rounded-md border border-white/10 bg-[#111a2c] pl-9 pr-3 text-sm outline-none focus:border-[#C2A462]/60" /></div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border border-white/10 bg-[#111a2c] px-2 text-sm" data-testid="library-status-filter">
          <option value="all">All statuses</option>{[...new Set(items.map((i) => i.status))].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {(q || cat !== "all" || status !== "all") && <Button size="sm" variant="ghost" onClick={() => { setQ(""); setCat("all"); setStatus("all"); }} data-testid="library-clear">Clear filters</Button>}
      </div>
      <div className="grid gap-5 lg:grid-cols-[200px_1fr]">
        <nav className="flex gap-1 overflow-x-auto lg:flex-col" data-testid="library-categories">
          {[["all", "All widgets", items.length], ...cats.map((c) => [c, c, items.filter((i) => i.category === c).length])].map(([k, l, n]) => (
            <button key={k} onClick={() => setCat(k)} data-testid={`library-cat-${k}`}
              className={`flex shrink-0 items-center justify-between gap-3 rounded-md px-3 py-1.5 text-left text-sm transition-colors ${cat === k ? "bg-[#C2A462]/15 text-[#EBD6A0]" : "text-slate-400 hover:bg-white/5"}`}>
              <span>{l}</span><span className="text-xs text-slate-500">{n}</span></button>
          ))}
        </nav>
        <div className={`grid gap-5 ${current ? "xl:grid-cols-[1fr_420px]" : ""}`}>
          {current && <div className="xl:order-2"><Detail item={current} dashboards={dashboards || []} onAdded={onAdded} onClose={() => setSel(null)} /></div>}
          <div className="xl:order-1">
            <div className="mb-3 text-xs text-slate-400" data-testid="library-count">{shown.length} widgets</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-3">
              {shown.map((i) => <Tile key={i.key} item={i} active={i.key === sel} onOpen={() => setSel(i.key === sel ? null : i.key)} />)}
            </div>
            {!shown.length && <p className="text-sm text-slate-500">No widgets match your filters.</p>}
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-500">Need help? Contact support@boshq.com.au</p>
    </div>
  );
}
